import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { Lock, RefreshCw, Trash2, Power, X, Shield, Smartphone } from 'lucide-react';

export const RemoteActionsPage = () => {
  const [devices, setDevices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // { device, action }
  const [processing, setProcessing] = useState(false);
  const [actionLog, setActionLog] = useState([]);

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) { setDevices(r.data); setFiltered(r.data); }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setFiltered(devices.filter(d => !searchQuery || d.deviceName?.toLowerCase().includes(searchQuery.toLowerCase()) || d.assignedTo?.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [searchQuery, devices]);

  const actions = [
    { id: 'lock', label: 'Remote Lock', icon: Lock, color: '#f59e0b', desc: 'Lock device screen immediately' },
    { id: 'wipe', label: 'Remote Wipe', icon: Trash2, color: '#ef4444', desc: 'Erase all data on device' },
    { id: 'restart', label: 'Restart Device', icon: RefreshCw, color: '#3b82f6', desc: 'Force restart the device' },
    { id: 'unenroll', label: 'Unenroll', icon: Power, color: '#6b7280', desc: 'Remove from MDM management' },
  ];

  const executeAction = async () => {
    setProcessing(true);
    let result;
    if (actionModal.action.id === 'lock') result = await deviceController.remoteLock(actionModal.device.id);
    else if (actionModal.action.id === 'wipe') result = await deviceController.remoteWipe(actionModal.device.id);
    else result = { success: true, message: `${actionModal.action.label} executed` };
    setActionLog(prev => [{ device: actionModal.device.deviceName, action: actionModal.action.label, time: new Date().toLocaleTimeString(), success: result.success }, ...prev.slice(0, 9)]);
    setActionModal(null);
    setProcessing(false);
  };

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#f59e0b20', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    layout: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '16px' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    actionBtns: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
    actBtn: { padding: '4px 10px', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' },
    logCard: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px' },
    logTitle: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' },
    logItem: { padding: '8px', borderRadius: '6px', backgroundColor: '#f8fafc', marginBottom: '6px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    mContent: { backgroundColor: '#fff', borderRadius: '14px', width: '90%', maxWidth: '400px' },
    mHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    mTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 },
    mBody: { padding: '24px' },
    mFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><Lock size={22} color="#f59e0b" /></div>
          <div><h1 style={s.title}>Remote Lock / Wipe</h1><div style={s.subtitle}>Execute remote actions on managed devices</div></div>
        </div>
        <div style={s.searchWrap || { position: 'relative' }}>
          <input type="text" placeholder="Filter devices..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '220px' }} />
        </div>
      </div>

      <div style={s.layout}>
        <div style={s.card}>
          {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div> : (
            <table style={s.table}>
              <thead><tr><th style={s.th}>Device</th><th style={s.th}>User</th><th style={s.th}>Status</th><th style={s.th}>Remote Actions</th></tr></thead>
              <tbody>
                {filtered.map(dev => (
                  <tr key={dev.id}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td style={s.td}>
                      <div style={{ fontWeight: '500' }}>{dev.deviceName}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{dev.platform} · {dev.serialNumber}</div>
                    </td>
                    <td style={s.td}>{dev.assignedTo || '—'}</td>
                    <td style={s.td}><span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '8px', backgroundColor: `${dev.getStatusColor()}15`, color: dev.getStatusColor() }}>{dev.status}</span></td>
                    <td style={s.td}>
                      <div style={s.actionBtns}>
                        {actions.map(action => (
                          <button key={action.id} style={{ ...s.actBtn, backgroundColor: `${action.color}15`, color: action.color }}
                            onClick={() => setActionModal({ device: dev, action })}>
                            <action.icon size={11} />{action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Action Log */}
        <div style={s.logCard}>
          <div style={s.logTitle}>Recent Actions</div>
          {actionLog.length === 0 ? <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px' }}>No actions yet</div> : actionLog.map((log, i) => (
            <div key={i} style={s.logItem}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#1e293b' }}>{log.action}</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>{log.device}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>{log.time}</span>
                <span style={{ fontSize: '10px', fontWeight: '600', color: log.success ? '#10b981' : '#ef4444' }}>{log.success ? 'Success' : 'Failed'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirm Modal */}
      {actionModal && (
        <div style={s.modal} onClick={() => setActionModal(null)}>
          <div style={s.mContent} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>Confirm Action</h2><button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setActionModal(null)}><X size={18} /></button></div>
            <div style={s.mBody}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: `${actionModal.action.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <actionModal.action.icon size={24} color={actionModal.action.color} />
                </div>
                <p style={{ fontWeight: '500', color: '#1e293b', marginBottom: '6px' }}>{actionModal.action.label} on "{actionModal.device.deviceName}"?</p>
                <p style={{ fontSize: '13px', color: '#64748b' }}>{actionModal.action.desc}</p>
                {actionModal.action.id === 'wipe' && <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '12px', color: '#ef4444', fontWeight: '500' }}>⚠️ This will permanently erase all data!</div>}
              </div>
            </div>
            <div style={s.mFooter}>
              <button style={{ padding: '9px 16px', backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }} onClick={() => setActionModal(null)}>Cancel</button>
              <button style={{ padding: '9px 16px', backgroundColor: actionModal.action.color, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }} onClick={executeAction} disabled={processing}>{processing ? 'Executing...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default RemoteActionsPage;
